import type { Category, GridCell, GridLayout, LayoutCell } from './types'

export const MIN_GRID = 2
export const MAX_GRID = 5

/**
 * The layout used to be a fixed 3x3 list keyed by cell index. It is now a grid
 * of arbitrary size whose cells can span more than one column or row, so the old
 * shape is migrated on read rather than in a batch job — a stored taxonomy from
 * before the change keeps working untouched.
 */
export function normaliseLayout(raw: GridLayout | LayoutCell[] | undefined | null): GridLayout {
  if (Array.isArray(raw)) return fromLegacy(raw)
  if (!raw || !Array.isArray(raw.cells)) return { columns: 3, rows: 3, cells: [] }

  const columns = clampSize(raw.columns)
  const rows = clampSize(raw.rows)
  const cells: GridCell[] = []
  const taken = new Set<string>()

  for (const cell of raw.cells) {
    const column = clamp(cell.column, 0, columns - 1)
    const row = clamp(cell.row, 0, rows - 1)
    const colSpan = clamp(cell.colSpan ?? 1, 1, columns - column)
    const rowSpan = clamp(cell.rowSpan ?? 1, 1, rows - row)
    const candidate: GridCell = { ...cell, column, row, colSpan, rowSpan }
    // A stored layout that somehow overlaps itself loses the later cell rather
    // than rendering two buttons on top of each other.
    if (coveredBy(candidate).some((key) => taken.has(key))) continue
    for (const key of coveredBy(candidate)) taken.add(key)
    cells.push(candidate)
  }
  return { columns, rows, cells: sortCells(cells) }
}

function fromLegacy(legacy: LayoutCell[]): GridLayout {
  return {
    columns: 3,
    rows: 3,
    cells: sortCells(
      legacy
        .filter((entry) => entry.cell >= 0 && entry.cell < 9)
        .map((entry) => ({
          id: `c${entry.cell}`,
          column: entry.cell % 3,
          row: Math.floor(entry.cell / 3),
          colSpan: 1,
          rowSpan: 1,
          categoryIds: entry.categoryIds,
        })),
    ),
  }
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, Math.round(value) || low))
}

function clampSize(value: number | undefined): number {
  return clamp(value ?? 3, MIN_GRID, MAX_GRID)
}

function key(column: number, row: number): string {
  return `${column}:${row}`
}

function coveredBy(cell: GridCell): string[] {
  const keys: string[] = []
  for (let c = cell.column; c < cell.column + cell.colSpan; c++) {
    for (let r = cell.row; r < cell.row + cell.rowSpan; r++) keys.push(key(c, r))
  }
  return keys
}

function sortCells(cells: GridCell[]): GridCell[] {
  return [...cells].sort((a, b) => a.row - b.row || a.column - b.column)
}

/** Every square covered by some cell. Used to find free space. */
export function occupancy(layout: GridLayout): Map<string, GridCell> {
  const map = new Map<string, GridCell>()
  for (const cell of layout.cells) {
    for (const k of coveredBy(cell)) map.set(k, cell)
  }
  return map
}

export function cellAt(layout: GridLayout, column: number, row: number): GridCell | undefined {
  return occupancy(layout).get(key(column, row))
}

export function newCellId(layout: GridLayout): string {
  const taken = new Set(layout.cells.map((cell) => cell.id))
  for (let n = 0; ; n++) {
    const candidate = `c${n}`
    if (!taken.has(candidate)) return candidate
  }
}

/** Put a category on the first free square, if there is one. */
export function addCell(layout: GridLayout, categoryId: string): GridLayout {
  const taken = occupancy(layout)
  for (let row = 0; row < layout.rows; row++) {
    for (let column = 0; column < layout.columns; column++) {
      if (taken.has(key(column, row))) continue
      return {
        ...layout,
        cells: sortCells([
          ...layout.cells,
          { id: newCellId(layout), column, row, colSpan: 1, rowSpan: 1, categoryIds: [categoryId] },
        ]),
      }
    }
  }
  return layout
}

export function removeCell(layout: GridLayout, id: string): GridLayout {
  return { ...layout, cells: layout.cells.filter((cell) => cell.id !== id) }
}

export function updateCell(layout: GridLayout, id: string, change: Partial<GridCell>): GridLayout {
  return {
    ...layout,
    cells: layout.cells.map((cell) => (cell.id === id ? { ...cell, ...change } : cell)),
  }
}

/**
 * Two cells can merge when they sit flush against each other and match on the
 * other axis — the same rule a spreadsheet uses, and what keeps the result a
 * rectangle.
 */
export function canMerge(a: GridCell, b: GridCell): boolean {
  const horizontally =
    a.row === b.row &&
    a.rowSpan === b.rowSpan &&
    (a.column + a.colSpan === b.column || b.column + b.colSpan === a.column)
  const vertically =
    a.column === b.column &&
    a.colSpan === b.colSpan &&
    (a.row + a.rowSpan === b.row || b.row + b.rowSpan === a.row)
  return horizontally || vertically
}

/** The merged cell keeps the first cell's categories and the union of its area. */
export function mergeCells(layout: GridLayout, aId: string, bId: string): GridLayout {
  const a = layout.cells.find((cell) => cell.id === aId)
  const b = layout.cells.find((cell) => cell.id === bId)
  if (!a || !b || a.id === b.id || !canMerge(a, b)) return layout

  const column = Math.min(a.column, b.column)
  const row = Math.min(a.row, b.row)
  const merged: GridCell = {
    ...a,
    column,
    row,
    colSpan: Math.max(a.column + a.colSpan, b.column + b.colSpan) - column,
    rowSpan: Math.max(a.row + a.rowSpan, b.row + b.rowSpan) - row,
  }
  return {
    ...layout,
    cells: sortCells([...layout.cells.filter((cell) => cell.id !== aId && cell.id !== bId), merged]),
  }
}

/** Break a spanning cell back into single squares; the original keeps its spot. */
export function unmergeCell(layout: GridLayout, id: string): GridLayout {
  const cell = layout.cells.find((candidate) => candidate.id === id)
  if (!cell || (cell.colSpan === 1 && cell.rowSpan === 1)) return layout
  return {
    ...layout,
    cells: sortCells([
      ...layout.cells.filter((candidate) => candidate.id !== id),
      { ...cell, colSpan: 1, rowSpan: 1 },
    ]),
  }
}

/**
 * Swap two cells' positions. Only defined for cells of the same shape, which
 * keeps reordering from silently dropping part of a merged cell.
 */
export function swapCells(layout: GridLayout, aId: string, bId: string): GridLayout {
  const a = layout.cells.find((cell) => cell.id === aId)
  const b = layout.cells.find((cell) => cell.id === bId)
  if (!a || !b || a.id === b.id) return layout
  if (a.colSpan !== b.colSpan || a.rowSpan !== b.rowSpan) return layout
  return {
    ...layout,
    cells: sortCells(
      layout.cells.map((cell) => {
        if (cell.id === aId) return { ...cell, column: b.column, row: b.row }
        if (cell.id === bId) return { ...cell, column: a.column, row: a.row }
        return cell
      }),
    ),
  }
}

/** Move a cell onto free space. Refuses to land on top of another cell. */
export function moveCell(layout: GridLayout, id: string, column: number, row: number): GridLayout {
  const cell = layout.cells.find((candidate) => candidate.id === id)
  if (!cell) return layout
  if (column < 0 || row < 0) return layout
  if (column + cell.colSpan > layout.columns || row + cell.rowSpan > layout.rows) return layout

  const moved = { ...cell, column, row }
  const others = layout.cells.filter((candidate) => candidate.id !== id)
  const taken = new Set(others.flatMap(coveredBy))
  if (coveredBy(moved).some((k) => taken.has(k))) return layout
  return { ...layout, cells: sortCells([...others, moved]) }
}

/** Resize the grid, dropping or shrinking whatever no longer fits. */
export function resizeGrid(layout: GridLayout, columns: number, rows: number): GridLayout {
  const nextColumns = clampSize(columns)
  const nextRows = clampSize(rows)
  const cells = layout.cells
    .filter((cell) => cell.column < nextColumns && cell.row < nextRows)
    .map((cell) => ({
      ...cell,
      colSpan: Math.min(cell.colSpan, nextColumns - cell.column),
      rowSpan: Math.min(cell.rowSpan, nextRows - cell.row),
    }))
  return { columns: nextColumns, rows: nextRows, cells: sortCells(cells) }
}

/** Archived categories come off the grid; a cell left with none disappears. */
export function pruneLayout(layout: GridLayout, categories: Category[]): GridLayout {
  const live = new Set(categories.filter((category) => !category.archived).map((c) => c.id))
  const cells: GridCell[] = []
  for (const cell of layout.cells) {
    const ids = cell.categoryIds.filter((id) => live.has(id))
    if (ids.length === 2) cells.push({ ...cell, categoryIds: [ids[0] as string, ids[1] as string] })
    else if (ids.length === 1) cells.push({ ...cell, categoryIds: [ids[0] as string] })
  }
  return { ...layout, cells }
}
